import {
  Briefcase,
  Calendar,
  CalendarDays,
  ChevronRight,
  Clock,
  IndianRupee,
  Timer,
} from "lucide-react";
import { useState } from "react";

const StaffEarnings = () => {
  const [selectedMonth, setSelectedMonth] = useState("September 2024");

  const earningsData = {
    monthlyEarnings: 12450,
    totalJobs: 32,
    todayJobs: 3,
    todayHours: 6.5,
    daysWorked: 18,
    avgHours: 5.8,
    avgEarningPerJob: 389,
    recentJobs: [
      {
        id: 1,
        customerName: "Anita Sharma",
        service: "Hair Spa",
        date: "12 Sep",
        time: "10:00 AM",
        amount: 850,
        status: "Completed",
      },
      {
        id: 2,
        customerName: "Riya Das",
        service: "Bridal Makeup",
        date: "10 Sep",
        time: "2:30 PM",
        amount: 2500,
        status: "Completed",
      },
      {
        id: 3,
        customerName: "Priya Patel",
        service: "Facial Cleanup",
        date: "09 Sep",
        time: "4:00 PM",
        amount: 600,
        status: "Completed",
      },
      {
        id: 4,
        customerName: "Meena Kumar",
        service: "Manicure & Pedicure",
        date: "08 Sep",
        time: "11:00 AM",
        amount: 1200,
        status: "Completed",
      },
    ],
  };

  const stats = [
    {
      id: "todayJobs",
      label: "Jobs Today",
      value: earningsData.todayJobs,
      icon: Briefcase,
      color: "bg-blue-50 text-blue-600",
      type: "daily",
    },
    {
      id: "todayHours",
      label: "Hours Worked",
      value: `${earningsData.todayHours}h`,
      icon: Clock,
      color: "bg-green-50 text-green-600",
      type: "daily",
    },
    {
      id: "daysWorked",
      label: "Days Worked",
      value: earningsData.daysWorked,
      icon: CalendarDays,
      color: "bg-purple-50 text-purple-600",
      type: "monthly",
    },
    {
      id: "avgHours",
      label: "Avg Hours / Day",
      value: `${earningsData.avgHours}h`,
      icon: Timer,
      color: "bg-orange-50 text-orange-600",
      type: "monthly",
    },
    {
      id: "avgEarning",
      label: "Avg Earning / Job",
      value: earningsData.avgEarningPerJob,
      icon: IndianRupee,
      color: "bg-emerald-50 text-emerald-600",
      type: "monthly",
    },
  ];

  const months = ["September 2024", "August 2024", "July 2024"];

  const handleJobClick = (jobId) => {
    console.log(`View details for job ${jobId}`);
  };

  const handleViewAllJobs = () => {
    console.log("View all completed jobs");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <h1 className="text-2xl font-bold text-primaryColor">Earnings</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Track your performance & income
          </p>
        </div>

        {/* Month Selector */}
        <div className="px-4 pb-3 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max">
            {months.map((month) => (
              <button
                key={month}
                onClick={() => setSelectedMonth(month)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedMonth === month
                    ? "bg-primaryColor text-white"
                    : "bg-gray-100 text-gray-700 active:bg-gray-200"
                }`}
              >
                {month}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-5 space-y-4 pb-20">
        {/* Monthly Earnings Card */}
        <div className="bg-gradient-to-br from-primaryColor to-[#5a0480] text-white rounded-2xl p-5 shadow-lg">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm text-white/80 mb-1">Total Earnings</p>
              <h2 className="text-4xl font-bold flex items-baseline gap-1">
                <IndianRupee size={28} className="mb-1" />
                {earningsData.monthlyEarnings.toLocaleString("en-IN")}
              </h2>
            </div>
            {/* <div className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-full">
              <TrendingUp size={14} />
              <span className="text-xs font-semibold">+12%</span>
            </div> */}
          </div>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Briefcase size={16} />
            <span>{earningsData.totalJobs} jobs completed this month</span>
          </div>
        </div>

        {/* Performance Grid */}
        <div className="grid grid-cols-5 gap-4">
          {/* Daily Stats */}
          <div className="col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 px-1">
              Today’s Activity
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {stats
                .filter((s) => s.type === "daily")
                .map(({ id, label, value, icon: Icon, color }) => (
                  <div
                    key={id}
                    className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4"
                  >
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center ${color}`}
                    >
                      <Icon size={20} />
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="text-xl font-bold text-gray-900">{value}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Monthly Stats */}
          <div className="col-span-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 px-1">
              Monthly Overview
            </h3>

            <div className="grid grid-cols-3 gap-4">
              {stats
                .filter((s) => s.type === "monthly")
                .map(({ id, label, value, icon: Icon, color }) => (
                  <div
                    key={id}
                    className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4"
                  >
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center ${color}`}
                    >
                      <Icon size={20} />
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="text-xl font-bold text-gray-900">{value}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Recent Jobs */}
        <div>
          <div className="flex justify-between items-center mb-3 px-1">
            <h3 className="text-sm font-semibold text-gray-900">Recent Jobs</h3>
            <button
              onClick={handleViewAllJobs}
              className="text-xs font-medium text-primaryColor active:text-[#350349] transition-colors"
            >
              View All
            </button>
          </div>

          <div className="space-y-2">
            {earningsData.recentJobs.map((job) => (
              <button
                key={job.id}
                onClick={() => handleJobClick(job.id)}
                className="w-full bg-white border border-gray-200 rounded-2xl p-4 shadow-sm active:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900 text-sm mb-0.5">
                      {job.customerName}
                    </h4>
                    <p className="text-sm text-gray-600">{job.service}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-gray-900 flex items-baseline gap-0.5 justify-end">
                      <IndianRupee size={14} />
                      <span className="text-base">
                        {job.amount.toLocaleString("en-IN")}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      <span>{job.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{job.time}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Payment Info */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 flex-shrink-0 mt-0.5">
              <IndianRupee size={16} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                Payment Schedule
              </h4>
              <p className="text-xs text-gray-600 leading-relaxed">
                Your earnings are processed on the 5th of every month and
                transferred to your registered bank account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffEarnings;
